(() => {
  if (!customElements.get('hydros-reviews')) customElements.define('hydros-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      const rail = this.querySelector('.hr-rail');
      if (!rail) return;
      this.events = new AbortController();
      const { signal } = this.events;
      const cards = [...rail.querySelectorAll('.hr-card')];
      const filters = [...this.querySelectorAll('[data-review-filter]')];
      const prev = this.querySelector('[data-review-prev]');
      const next = this.querySelector('[data-review-next]');
      const status = this.querySelector('[data-review-status]');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const visible = () => cards.filter(card => !card.hidden);
      const sync = () => {
        const items = visible();
        const bounds = rail.getBoundingClientRect();
        const positions = items.map(card => card.getBoundingClientRect());
        const first = Math.max(0, positions.findIndex(rect => rect.right > bounds.left + 8));
        let last = first;
        positions.forEach((rect, index) => { if (rect.left < bounds.right - 8) last = index; });
        prev.disabled = rail.scrollLeft < 4;
        next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
        const label = `${first + 1}${last > first ? `–${last + 1}` : ''} / ${items.length}`;
        if (status.textContent !== label) status.textContent = label;
      };
      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; sync(); });
      };
      const move = (direction, event) => {
        const items = visible();
        if (!items.length) return;
        const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
        rail.scrollBy({ left: direction * (items[0].getBoundingClientRect().width + gap), behavior: event.detail && !reduced.matches ? 'smooth' : 'instant' });
      };
      filters.forEach(button => button.addEventListener('click', () => {
        const key = button.dataset.reviewFilter;
        cards.forEach(card => { card.hidden = key !== 'all' && card.dataset.reviewScope !== key; });
        filters.forEach(filter => filter.setAttribute('aria-pressed', String(filter === button)));
        rail.scrollTo({ left: 0, behavior: 'instant' });
        sync();
      }, { signal }));
      prev.addEventListener('click', event => move(-1, event), { signal });
      next.addEventListener('click', event => move(1, event), { signal });
      rail.addEventListener('scroll', schedule, { passive: true, signal });
      rail.addEventListener('keydown', event => {
        if (event.target !== rail || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') rail.scrollTo({ left: event.key === 'Home' ? 0 : rail.scrollWidth, behavior: 'instant' });
        else move(event.key === 'ArrowRight' ? 1 : -1, { detail: 0 });
      }, { signal });
      this.resize = new ResizeObserver(schedule);
      this.resize.observe(rail);
      this.querySelector('[data-review-controls]').hidden = false;
      sync();
    }
    disconnectedCallback() {
      this.events?.abort(); this.events = null;
      this.resize?.disconnect(); cancelAnimationFrame(this.frame); this.frame = 0;
    }
  });

  if (!customElements.get('hydros-anatomy')) customElements.define('hydros-anatomy', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const { signal } = this.events;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const nodes = {};
      this.querySelectorAll('[data-anatomy-part]').forEach(node => { nodes[node.dataset.anatomyPart] = node; });
      const copies = [...this.querySelectorAll('[data-anatomy-copy]')];
      const progress = this.querySelector('.ha-progress > span');
      const isHynox = this.dataset.product === 'hynox';
      const staticImage = this.querySelector('.ha-product-image');
      const capOverlay = this.querySelector('[data-anatomy-cap]');
      const isStatic = this.dataset.anatomyStatic === 'true';
      const clamp = value => Math.min(1, Math.max(0, value));
      const draw = value => {
        const p = clamp(value);
        if (isStatic) {
          if (staticImage) {
            const lumosMotion = this.dataset.product === 'lumos' && this.dataset.anatomyMotion === 'scroll';
            const scale = lumosMotion ? 1 + p * .07 : 1 + p * .025;
            const rotation = lumosMotion ? (1 - p) * -2.2 : 0;
            staticImage.style.transform = `scale(${scale}) rotate(${rotation}deg)`;
            if (capOverlay) {
              const lift = (1 - p) * -15;
              const capRotation = (1 - p) * -4;
              capOverlay.style.transform = `translateY(${lift}%) rotate(${capRotation}deg) scale(${scale})`;
            }
            this.style.setProperty('--anatomy-progress', p.toFixed(3));
          }
          progress.style.transform = `scaleX(${p})`;
          const index = Math.min(copies.length - 1, Math.floor(p * copies.length));
          copies.forEach((copy, i) => copy.classList.toggle('is-current', i === index));
          this.dataset.progress = p.toFixed(3);
          return;
        }
        if (isHynox) {
          nodes.cap.style.transform = `translate(${-45 * (1 - p)}px, ${180 * (1 - p) - p * 25}px)`;
          nodes.straw.style.transform = `translate(${230 * (1 - p)}px, ${240 * (1 - p)}px)`;
          nodes.filter.style.transform = `translate(${-250 * (1 - p)}px, ${220 * (1 - p)}px)`;
          nodes.straw.style.opacity = String(clamp((p - .05) / .35));
          nodes.filter.style.opacity = String(clamp((p - .15) / .35));
        } else {
          nodes.cap.style.transform = `translate(${28 * p}px, ${-115 * p}px)`;
          nodes.body.style.transform = `translateY(${20 * p}px)`;
        }
        progress.style.transform = `scaleX(${p})`;
        const index = Math.min(copies.length - 1, Math.floor(p * copies.length));
        copies.forEach((copy, i) => copy.classList.toggle('is-current', i === index));
        this.dataset.progress = p.toFixed(3);
      };
      const update = () => {
        this.frame = 0;
        if (reduced.matches || document.hidden) return;
        const bounds = this.getBoundingClientRect();
        if (!bounds.height) return;
        const travel = Math.max(360, bounds.height - innerHeight * .35);
        draw((innerHeight * .35 - bounds.top) / travel);
      };
      const schedule = () => { if (!this.frame && !reduced.matches && this.near) this.frame = requestAnimationFrame(update); };
      const syncPreference = () => {
        cancelAnimationFrame(this.frame); this.frame = 0;
        this.classList.toggle('is-motion', !reduced.matches);
        if (reduced.matches) draw(1);
        else { update(); schedule(); }
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      document.addEventListener('visibilitychange', schedule, { signal });
      reduced.addEventListener('change', syncPreference, { signal });
      this.observer = new IntersectionObserver(entries => {
        this.near = entries[0].isIntersecting;
        if (this.near) {
          this.querySelectorAll('[data-anatomy-src]').forEach(image => { image.setAttribute('href', image.dataset.anatomySrc); image.removeAttribute('data-anatomy-src'); });
          schedule();
        }
      }, { rootMargin: '600px' });
      this.observer.observe(this);
      this.resize = new ResizeObserver(schedule);
      this.resize.observe(this);
      syncPreference();
    }
    disconnectedCallback() {
      this.events?.abort(); this.events = null;
      this.observer?.disconnect(); this.resize?.disconnect();
      cancelAnimationFrame(this.frame); this.frame = 0;
    }
  });
})();
